
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Fund } from "@/hooks/useFundData";
import { logger } from "@/services/LoggingService";
import { useUserRole } from "@/hooks/useUserRole";
import {
  getCompanyQueryKey,
  handleOptimisticUpdate,
  revertOptimisticUpdate
} from "./mutations/companyLogoCache";

const COMPONENT = 'CompanyLogoMutation';

interface LogoMutationContext {
  previousData: Fund | undefined;
  queryKey: readonly unknown[];
}

export function useLogoMutation(companyId: string) {
  const queryClient = useQueryClient();
  const queryKey = getCompanyQueryKey(companyId);
  const { userRole } = useUserRole();

  return useMutation({
    mutationFn: async (logoUrl: string) => {
      logger.info(COMPONENT, 'Starting logo update', {
        context: {
          companyId,
          logoUrl,
          userRole,
          timestamp: new Date().toISOString()
        }
      });

      if (!companyId) throw new Error("No company ID provided");

      // Check if user has permission to update logos
      if (userRole !== 'sys_admin' && userRole !== 'manager') {
        const error = new Error("You don't have permission to update company logos");
        logger.error(COMPONENT, 'Permission denied', {
          userRole,
          companyId,
          timestamp: new Date().toISOString()
        });
        throw error;
      }

      const { data, error } = await supabase
        .from('fund_companies')
        .update({ logo_url: logoUrl })
        .eq('id', companyId)
        .select()
        .single();

      if (error) {
        logger.error(COMPONENT, 'Operation failed', {
          error: error.message,
          details: error,
          context: {
            companyId,
            logoUrl,
            userRole,
            timestamp: new Date().toISOString()
          }
        });
        throw error;
      }

      return data;
    },
    onMutate: async (newLogoUrl) => {
      return handleOptimisticUpdate(queryClient, companyId, newLogoUrl);
    },
    onError: (error, _, context) => {
      logger.error(COMPONENT, 'Operation failed', {
        error: error.message,
        context: {
          companyId,
          userRole,
          timestamp: new Date().toISOString()
        }
      });

      if (context) {
        revertOptimisticUpdate(queryClient, companyId, context.previousData);
      }
      toast.error(error.message || "Failed to update logo");
    },
    onSuccess: (data) => {
      toast.success("Logo updated successfully");
      queryClient.invalidateQueries({ queryKey });
    }
  });
}
